"use client"

import { useEffect, useRef, useState } from "react"
import { useSearch } from "../../../service/SearchContext"
import axios from "axios"
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  BookOpen,
  MessageSquare,
  Users,
  Globe,
  CheckCircle,
  AlertCircle,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react"
import { API_BASE_URL } from "../../../config/config"

// Données de contact
const contactInfo = [
  {
    id: 1,
    icon: "Mail",
    title: "Email",
    details: "contact@kenya-guide.com",
    description: "Réponse sous 24h",
    color: "bg-green-100 text-green-800",
  },
  {
    id: 2,
    icon: "Phone",
    title: "Téléphone",
    details: "--- --- ---",
    description: "Lun-Ven 9h-17h (EAT)",
    color: "bg-blue-100 text-blue-800",
  },
  {
    id: 3,
    icon: "MapPin",
    title: "Adresse",
    details: "Nairobi, Kenya",
    description: "Afrique de l'Est",
    color: "bg-red-100 text-red-800",
  },
  {
    id: 4,
    icon: "Clock",
    title: "Horaires",
    details: "24h/7j",
    description: "Support en ligne",
    color: "bg-yellow-100 text-yellow-800",
  },
]

const faqTopics = [
  {
    id: 1,
    icon: "BookOpen",
    title: "Contenu Éducatif",
    description: "Questions sur le matériel d'étude",
    count: "45+ questions",
  },
  {
    id: 2,
    icon: "Users",
    title: "Collaboration",
    description: "Partenariats et contributions",
    count: "12+ partenaires",
  },
  {
    id: 3,
    icon: "Globe",
    title: "Informations Générales",
    description: "Questions sur le Kenya",
    count: "100+ réponses",
  },
  {
    id: 4,
    icon: "MessageSquare",
    title: "Support Technique",
    description: "Aide avec le site web",
    count: "Support 24/7",
  },
]

const socialLinks = [
  {
    id: 1,
    name: "Facebook",
    icon: "Facebook",
    url: "#",
    color: "bg-blue-600 hover:bg-blue-700",
  },
  {
    id: 2,
    name: "Twitter",
    icon: "Twitter",
    url: "#",
    color: "bg-sky-500 hover:bg-sky-600",
  },
  {
    id: 3,
    name: "Instagram",
    icon: "Instagram",
    url: "#",
    color: "bg-pink-600 hover:bg-pink-700",
  },
  {
    id: 4,
    name: "LinkedIn",
    icon: "Linkedin",
    url: "#",
    color: "bg-blue-700 hover:bg-blue-800",
  },
]

// Composants UI
const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium"
  const variants = {
    default: "bg-green-100 text-green-800",
    secondary: "bg-gray-100 text-gray-800",
    outline: "border dark:border-gray-600 border-gray-900 text-gray-700 bg-white",
    success: "bg-green-100 text-green-800",
    error: "bg-red-100 text-red-800",
  }
  return <span className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</span>
}

const Card = ({ children, className = "" }) => {
  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm rounded ${className}`}
    >
      {children}
    </div>
  )
}

const CardHeader = ({ children }) => {
  return <div className="p-6 pb-4">{children}</div>
}

const CardContent = ({ children }) => {
  return <div className="px-6 pb-6">{children}</div>
}

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3 className={`text-lg font-semibold leading-none tracking-tight dark:text-white ${className}`}>{children}</h3>
  )
}

const CardDescription = ({ children, className = "" }) => {
  return <p className={`text-sm text-gray-600 dark:text-gray-300 ${className}`}>{children}</p>
}

const Input = ({ className = "", ...props }) => {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white ${className}`}
      {...props}
    />
  )
}

const Textarea = ({ className = "", ...props }) => {
  return (
    <textarea
      className={`flex min-h-[80px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white ${className}`}
      {...props}
    />
  )
}

const Button = ({ children, variant = "default", className = "", onClick, disabled = false, ...props }) => {
  const baseClasses =
    "inline-flex items-center justify-center text-sm font-medium transition-colors rounded focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
  const variants = {
    default: "bg-green-800 text-white hover:bg-green-700 h-10 px-4 py-2",
    outline:
      "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 h-10 px-4 py-2 text-gray-900 dark:text-white",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 h-10 px-4 py-2",
    destructive: "bg-red-600 text-white hover:bg-red-700 h-10 px-4 py-2",
  }
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
}

const Label = ({ children, htmlFor, className = "" }) => {
  return (
    <label
      htmlFor={htmlFor}
      className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 dark:text-white ${className}`}
    >
      {children}
    </label>
  )
}

// Fonction pour rendre les icônes
const renderIcon = (iconName, className = "w-5 h-5") => {
  const icons = {
    Mail: <Mail className={className} />,
    Phone: <Phone className={className} />,
    MapPin: <MapPin className={className} />,
    Clock: <Clock className={className} />,
    Send: <Send className={className} />,
    BookOpen: <BookOpen className={className} />,
    MessageSquare: <MessageSquare className={className} />,
    Users: <Users className={className} />,
    Globe: <Globe className={className} />,
    CheckCircle: <CheckCircle className={className} />,
    AlertCircle: <AlertCircle className={className} />,
    Facebook: <Facebook className={className} />,
    Twitter: <Twitter className={className} />,
    Instagram: <Instagram className={className} />,
    Linkedin: <Linkedin className={className} />,
  }
  return icons[iconName] || <Mail className={className} />
}

function Contact() {
  // Function pour faire une recherche et faire un scrolle directement sur la parti du mot en utilisant un ref={contactRef}

  const { searchTerm } = useSearch()
  const contactRef = useRef()
  useEffect(() => {
    if (
      searchTerm &&
      contactRef.current &&
      contactRef.current.innerText.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      contactRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [searchTerm])

  // =============================>

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    num_waht: "",
    message: "",
  })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = "Le nom est requis"
    if (!formData.email.trim()) newErrors.email = "L'email est requis"
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email invalide"
    if (!formData.num_waht.trim()) newErrors.num_waht = "Le numéro WhatsApp est requis"
    if (!formData.message.trim()) newErrors.message = "Le message est requis"
    if (formData.message.length < 10) newErrors.message = "Le message doit contenir au moins 10 caractères"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)

    try {
      const response = await axios.post(`${API_BASE_URL}/message`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      })

      setIsLoading(false)
      setIsSubmitted(true)
      setFormData({ 

        name: "", 
        email: "", 
        num_waht: "", 
        message: "" 
      
      })
    } catch (error) {
      setIsLoading(false)
      setErrors({
        submit: "Erreur lors de l'envoi du message. Veuillez réessayer.",
      })
      console.error("Error submitting form:", error)
    }
  }

  const resetForm = () => {
    setIsSubmitted(false)
    setErrors({})
  }

  return (
    <div className="min-h-screen mtCGfreeUniv  from-green-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      {/* <header className="bg-white dark:bg-gray-900 shadow-sm border-b-4 border-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-red-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Guide d'Étude Kenya</h1>
                <p className="text-gray-600 dark:text-gray-300 mt-1">Page de Contact</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-8 h-5 bg-black"></div>
              <div className="w-8 h-5 bg-red-600"></div>
              <div className="w-8 h-5 bg-green-600"></div>
            </div>
          </div>
        </div>
      </header> */}

      {/* Hero Section */}
      <section className="py-16 from-green-600 via-red-600 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl text-gray-900 dark:text-gray-200 font-bold mb-4">Contactez-Nous</h2>
          <p className="text-xl text-gray-900 dark:text-gray-100 font-bold max-w-2xl mx-auto">
            Une question sur le Kenya ? Besoin d'aide avec votre étude ? Notre équipe est là pour vous accompagner.
          </p>
        </div>
      </section>

      {/* Statistiques de Contact */}
      <section className=" dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">24h</div>
              <div className="text-gray-600 dark:text-gray-300">Temps de réponse</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">98%</div>
              <div className="text-gray-600 dark:text-gray-300">Satisfaction client</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">7j/7</div>
              <div className="text-gray-600 dark:text-gray-300">Support disponible</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contenu Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulaire de Contact */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  {renderIcon("Send", "w-6 h-6 text-green-500 dark:text-door-dark")}
                  Envoyez-nous un message
                </CardTitle>
                <CardDescription className="mt-5">
                  Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="text-center py-8">
                    {renderIcon("CheckCircle", "w-16 h-16 text-green-600 mx-auto mb-4")}
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Message envoyé !</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                     Merci de votre prise de contact. Une réponse vous sera transmise prochainement par courrier électronique ou via WhatsApp sous 24h.
                    </p>
                    <Badge variant="success" className="mb-4">
                      Référence: MSG-{Math.random().toString(36).substr(2, 9).toUpperCase()}
                    </Badge>
                    <div>
                      <Button onClick={resetForm} className="mt-4">
                        Envoyer un autre message
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {errors.submit && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-red-600 dark:text-red-400 text-sm flex items-center">
                          {renderIcon("AlertCircle", "w-4 h-4 mr-2")}
                          {errors.submit}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom complet </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Votre nom"
                          className={errors.name ? "border-red-500" : ""}
                        />
                        {errors.name && (
                          <p className="text-red-500 text-xs flex items-center">
                            {renderIcon("AlertCircle", "w-3 h-3 mr-1")}
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="votre@email.com"
                          className={errors.email ? "border-red-500" : ""}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs flex items-center">
                            {renderIcon("AlertCircle", "w-3 h-3 mr-1")}
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="num_waht">Numéro WhatsApp </Label>
                      <Input
                        id="num_waht"
                        name="num_waht"
                        type="tel"
                        value={formData.num_waht}
                        onChange={handleInputChange}
                        placeholder="+254 700 123 456"
                        className={errors.num_waht ? "border-red-500" : ""}
                      />
                      {errors.num_waht && (
                        <p className="text-red-500 text-xs flex items-center">
                          {renderIcon("AlertCircle", "w-3 h-3 mr-1")}
                          {errors.num_waht}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Décrivez votre question ou demande..."
                        rows={6}
                        spellCheck={true}
                        className={errors.message ? "border-red-500" : ""}
                      />
                      <div className="flex justify-between items-center">
                        {errors.message ? (
                          <p className="text-red-500 text-xs flex items-center">
                            {renderIcon("AlertCircle", "w-3 h-3 mr-1")}
                            {errors.message}
                          </p>
                        ) : (
                          <p className="text-gray-500 text-xs">{formData.message.length}/500 caractères</p>
                        )}
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          {renderIcon("Send", "w-4 h-4 mr-2")}
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Informations de Contact */}
          <div className="space-y-6">
            {/* Coordonnées */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle ref={contactRef} className="text-xl">
                  Informations de Contact
                </CardTitle>
                <CardDescription className="mt-5">Plusieurs moyens de nous joindre</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {contactInfo.map((info) => (
                  <div
                    key={info.id}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className={`p-2 rounded-full ${info.color.replace("text-", "text-").replace("bg-", "bg-")}`}>
                      {renderIcon(info.icon, "w-4 h-4")}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-white">{info.title}</h4>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">{info.details}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{info.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Réseaux Sociaux */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">Suivez-nous</CardTitle>
                <CardDescription>Restez connecté sur nos réseaux sociaux</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-center space-x-2 p-3 rounded-lg text-white transition-colors ${social.color}`}
                    >
                      {renderIcon(social.icon, "w-4 h-4")}
                      <span className="text-sm font-medium">{social.name}</span>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Engagement de Service */}
            <Card className="shadow-lg bg-gradient-to-r from-green-600 to-red-600 text-white">
              <CardContent className="p-6 text-center">
                <h4 className="text-lg font-semibold mb-2">Notre Engagement</h4>
                <p className="text-sm mb-4">
                  Nous nous engageons à répondre à tous les messages dans les 24 heures et à fournir un support de
                  qualité.
                </p>
                <Badge variant="secondary" className="text-green-700">
                  Support Prioritaire
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section FAQ Rapide */}
        <div className="mt-12">
          <Card className="bg-blue-50 dark:bg-gray-800">
            <CardContent className="p-6">
              <h3 className="text-xl p-6 font-semibold mb-4 text-gray-900 dark:text-white">Questions Fréquentes</h3>
              <div className="grid -mt-5 grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">
                    Comment puis-je utiliser le guide d'étude ?
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Notre guide est organisé par thèmes. Utilisez la recherche et les filtres pour trouver les
                    informations spécifiques dont vous avez besoin.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">
                    Les informations sont-elles à jour ?
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Oui, nous mettons régulièrement à jour notre contenu pour garantir l'exactitude des informations sur
                    le Kenya.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">Puis-je contribuer au contenu ?</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Absolument ! Nous accueillons les contributions. Contactez-nous pour discuter de vos idées et
                    suggestions.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2">Y a-t-il des frais d'utilisation ?</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Non, notre guide d'étude est entièrement gratuit et accessible à tous les étudiants et passionnés du
                    Kenya.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Contact
